/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';
import LineItemFishingFeeMaybe from './LineItemFishingFeeMaybe';
import LineItemCampingFeeMaybe from './LineItemCampingFeeMaybe';
import LineItemDiscountMaybe from './LineItemDiscountMaybe';
import LineItemLodgingFeeMaybe from './LineItemLodgingFeeMaybe';
import LineItemCampingPerBookingFeeMaybe from './LineItemCampingPerBookingFeeMaybe';
import LineItemGiftCardMaybe from './LineItemGiftCardMaybe';
import LineItemSeasonPassMaybe from './LineItemSeasonPassMaybe';
import LineItemBuddyPassMaybe from './LineItemBuddyPassMaybe';
import LineItemTaxesAndServiceFeeMaybe from './LineItemTaxesAndServiceFeeMaybe';
import LineItemTaxesAndServiceFeeDiscountMaybe from './LineItemTaxesAndServiceFeeDiscountMaybe';
import LineItemGuideFeeMaybe from './LineItemGuideFeeMaybe';

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    currentListing,
    onManageDisableScrolling,
    onDeleteGiftCard,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const lineItems = transaction.attributes.lineItems;
  const unitLineItem = lineItems?.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  const campingFeePerDayPerPerson =
    currentListing?.attributes.publicData.campingFee?.campingPerDayPerPerson;

  const lodgingFeePerDayPerPerson =
    currentListing?.attributes.publicData.lodgingFee?.lodgingPerDayPerPerson;

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      <LineItemFishingFeeMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />

      {transaction.attributes.processName === 'season-pass-purchase' ||
      transaction.attributes.processName === 'zero-payment-season-pass-purchase' ? (
        <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      ) : null}

      <LineItemCampingFeeMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
      <LineItemCampingPerBookingFeeMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        intl={intl}
        campingFeePerDayPerPerson={campingFeePerDayPerPerson}
      />
      <LineItemLodgingFeeMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        intl={intl}
        lodgingFeePerDayPerPerson={lodgingFeePerDayPerPerson}
      />
      <LineItemGuideFeeMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
      <LineItemSeasonPassMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      <LineItemBuddyPassMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      <LineItemDiscountMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
      <LineItemTaxesAndServiceFeeMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
      <LineItemGiftCardMaybe
        onManageDisableScrolling={onManageDisableScrolling}
        lineItems={lineItems}
        code={lineItemUnitType}
        intl={intl}
        onDeleteGiftCard={onDeleteGiftCard}
      />

      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />

      {/* TODO-GB: no need? */}
      {/* <LineItemTaxesAndServiceFeeDiscountMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        intl={intl}
      /> */}

      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />

      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
  onDeleteGiftCard: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
